import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Goal,
  AlertTriangle,
  Plus,
  X,
  Ban,
  Users,
  Camera,
  Image,
} from 'lucide-react';

const MatchReportDialog = ({
  selectedMatch,
  clubs,
  homePlayers,
  awayPlayers,
  isSuperAdmin,
  userClubId,
  isItalian,
  reportForm,
  setReportForm,
  submittingReport,
  photoInputRef,
  onSubmit,
  onClose,
  onPhotoUpload,
  onRemovePhoto,
}) => {
  const getClub = (clubId) => clubs.find((c) => c.club_id === clubId);

  const addEvent = (type) => {
    setReportForm((prev) => ({
      ...prev,
      events: [...prev.events, { player_id: '', event_type: type, minute: 1 }],
    }));
  };

  const updateEvent = (index, field, value) => {
    setReportForm((prev) => ({
      ...prev,
      events: prev.events.map((e, i) =>
        i === index ? { ...e, [field]: field === 'minute' ? (parseInt(value) || 1) : value } : e
      ),
    }));
  };

  const removeEvent = (index) => {
    setReportForm((prev) => ({
      ...prev,
      events: prev.events.filter((_, i) => i !== index),
    }));
  };

  return (
    <Dialog open={!!selectedMatch} onOpenChange={() => onClose()}>
      <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-wider">
            {isItalian ? 'Invia Report' : 'Submit Report'}
          </DialogTitle>
        </DialogHeader>
        {selectedMatch && (
          <div className="space-y-6 mt-4">
            {/* Score */}
            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">
                  {getClub(selectedMatch.home_club_id)?.name}
                </p>
                <Input
                  type="number"
                  min={0}
                  value={reportForm.home_score === '' ? '' : reportForm.home_score}
                  onChange={(e) =>
                    setReportForm((prev) => ({
                      ...prev,
                      home_score: e.target.value === '' ? '' : parseInt(e.target.value),
                    }))
                  }
                  onBlur={(e) =>
                    setReportForm((prev) => ({
                      ...prev,
                      home_score: e.target.value === '' ? 0 : parseInt(e.target.value),
                    }))
                  }
                  className="bg-[#050508] border-white/10 text-white text-center font-mono text-2xl"
                  data-testid="home-score-input"
                />
              </div>
              <div className="text-center text-2xl font-bold text-slate-500">-</div>
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">
                  {getClub(selectedMatch.away_club_id)?.name}
                </p>
                <Input
                  type="number"
                  min={0}
                  value={reportForm.away_score === '' ? '' : reportForm.away_score}
                  onChange={(e) =>
                    setReportForm((prev) => ({
                      ...prev,
                      away_score: e.target.value === '' ? '' : parseInt(e.target.value),
                    }))
                  }
                  onBlur={(e) =>
                    setReportForm((prev) => ({
                      ...prev,
                      away_score: e.target.value === '' ? 0 : parseInt(e.target.value),
                    }))
                  }
                  className="bg-[#050508] border-white/10 text-white text-center font-mono text-2xl"
                  data-testid="away-score-input"
                />
              </div>
            </div>

            {/* Events */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                    {isItalian ? 'Eventi' : 'Events'}
                  </Label>
                  {!isSuperAdmin && userClubId && (
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {isItalian ? 'Inserisci solo gol e assist della tua squadra' : 'Enter only your team\'s goals and assists'}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button type="button" size="sm" variant="outline" className="text-[#00E676] border-[#00E676]/30 hover:bg-[#00E676]/10" onClick={() => addEvent('goal')} data-testid="add-goal-event">
                    <Goal size={14} className="mr-1" /> {isItalian ? 'Gol' : 'Goal'}
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="text-[#00F0FF] border-[#00F0FF]/30 hover:bg-[#00F0FF]/10" onClick={() => addEvent('assist')} data-testid="add-assist-event">
                    <Plus size={14} className="mr-1" /> Assist
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="text-[#FFEA00] border-[#FFEA00]/30 hover:bg-[#FFEA00]/10" onClick={() => addEvent('yellow_card')} data-testid="add-yellow-card-event">
                    <AlertTriangle size={14} className="mr-1" /> {isItalian ? 'Giallo' : 'Yellow'}
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="text-[#FF1744] border-[#FF1744]/30 hover:bg-[#FF1744]/10" onClick={() => addEvent('red_card')} data-testid="add-red-card-event">
                    <Ban size={14} className="mr-1" /> {isItalian ? 'Rosso' : 'Red'}
                  </Button>
                </div>
              </div>

              <div className="space-y-3 max-h-48 overflow-y-auto">
                {reportForm.events.map((event, index) => {
                  const isUserHome = userClubId === selectedMatch.home_club_id;
                  const isUserAway = userClubId === selectedMatch.away_club_id;
                  const showHome = isSuperAdmin || isUserHome;
                  const showAway = isSuperAdmin || isUserAway;
                  const homeClub = getClub(selectedMatch.home_club_id);
                  const awayClub = getClub(selectedMatch.away_club_id);

                  return (
                    <div key={index} className="flex items-center gap-2 bg-[#050508] border border-white/10 p-2">
                      <Select value={event.player_id} onValueChange={(value) => updateEvent(index, 'player_id', value)}>
                        <SelectTrigger className="flex-1 bg-transparent border-0 text-white" data-testid={`event-player-${index}`}>
                          <SelectValue placeholder={isItalian ? 'Seleziona giocatore' : 'Select player'} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0d15] border-white/10 max-h-60">
                          {showHome && homePlayers.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-bold text-[#00F0FF] uppercase flex items-center gap-2 border-b border-white/10">
                                <Users size={12} /> {homeClub?.name || 'Casa'}
                              </div>
                              {homePlayers.map((player) => (
                                <SelectItem key={player.user_id} value={player.user_id}>
                                  {player.gamertag || player.name}
                                </SelectItem>
                              ))}
                            </>
                          )}
                          {showAway && awayPlayers.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-bold text-[#FF1744] uppercase flex items-center gap-2 border-b border-white/10 mt-2">
                                <Users size={12} /> {awayClub?.name || 'Ospiti'}
                              </div>
                              {awayPlayers.map((player) => (
                                <SelectItem key={player.user_id} value={player.user_id}>
                                  {player.gamertag || player.name}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      <span className={`px-2 py-1 text-xs rounded ${
                        event.event_type === 'goal' ? 'bg-[#00E676]/20 text-[#00E676]'
                        : event.event_type === 'assist' ? 'bg-[#00F0FF]/20 text-[#00F0FF]'
                        : event.event_type === 'yellow_card' ? 'bg-[#FFEA00]/20 text-[#FFEA00]'
                        : 'bg-[#FF1744]/20 text-[#FF1744]'
                      }`}>
                        {event.event_type === 'goal' && (isItalian ? 'Gol' : 'Goal')}
                        {event.event_type === 'assist' && 'Assist'}
                        {event.event_type === 'yellow_card' && (isItalian ? 'Giallo' : 'Yellow')}
                        {event.event_type === 'red_card' && (isItalian ? 'Rosso' : 'Red')}
                      </span>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <Input
                          type="number" min={1} max={120}
                          value={event.minute}
                          onChange={(e) => updateEvent(index, 'minute', parseInt(e.target.value))}
                          className="w-14 bg-transparent border-0 text-white text-center font-mono"
                          placeholder="--"
                        />
                        <span className="text-[10px] text-slate-500 font-mono">min</span>
                      </div>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeEvent(index)} className="text-slate-500 hover:text-[#FF1744]">
                        <X size={16} />
                      </Button>
                    </div>
                  );
                })}
                {reportForm.events.length === 0 && (
                  <div className="text-center py-6 text-slate-500 text-sm">
                    {isItalian ? 'Aggiungi eventi della partita (gol, assist, cartellini)' : 'Add match events (goals, assists, cards)'}
                  </div>
                )}
              </div>
            </div>

            {/* Photo Upload */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold flex items-center gap-2">
                  <Camera size={14} />
                  {isItalian ? 'Foto Partita' : 'Match Photos'}
                </Label>
                <span className="text-xs text-slate-500">{reportForm.photos.length}/5</span>
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onPhotoUpload}
                data-testid="photo-upload-input"
              />
              <div className="flex flex-wrap gap-2 mb-2">
                {reportForm.photos.map((photo, index) => (
                  <div key={index} className="relative group w-20 h-20 rounded border border-white/10 overflow-hidden">
                    <img src={photo} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => onRemovePhoto(index)}
                      className="absolute top-0 right-0 bg-[#FF1744] text-white p-0.5 rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${index}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {reportForm.photos.length < 5 && (
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="w-20 h-20 rounded border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
                    data-testid="add-photo-btn"
                  >
                    <Image size={18} />
                    <span className="text-[10px]">{isItalian ? 'Aggiungi' : 'Add'}</span>
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {isItalian ? 'Max 5 foto, 5MB ciascuna' : 'Max 5 photos, 5MB each'}
              </p>
            </div>

            <Button
              className="w-full btn-primary"
              onClick={onSubmit}
              disabled={submittingReport}
              data-testid="submit-match-report"
            >
              {submittingReport ? (
                <span className="flex items-center gap-2 justify-center">
                  <span className="w-4 h-4 border-2 border-[#050508]/30 border-t-[#050508] rounded-full animate-spin" />
                  {isItalian ? 'Invio in corso...' : 'Submitting...'}
                </span>
              ) : (
                isItalian ? 'Invia Report' : 'Submit Report'
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MatchReportDialog;
